import { useEffect, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import type { LucideIcon } from "lucide-react";
import {
  ArrowRight,
  BarChart3,
  Bell,
  Brain,
  Calendar,
  Camera,
  Cpu,
  Database,
  ExternalLink,
  Globe,
  Heart,
  Linkedin,
  Mail,
  MapPin,
  Search,
  Target,
  TrendingUp,
  Twitter,
} from "lucide-react";
import Meta from "../components/Meta";
import { PLANNING_CATEGORY_ARTWORK } from "../lib/planning/categoryArtwork";
import { PLANNING_ALERTS_ENABLED } from "../lib/planningAlertsFeatureFlag";
import { PLANNING_CURRENT_COVERAGE_COPY } from "../lib/planningSeo";

type AboutStats = {
  totalApplications: number;
  citizensInformed: number;
  locationsTracked: number;
  lastUpdated: string;
};

type Feature = {
  icon: LucideIcon;
  title: string;
  description: string;
};

const defaultStats: AboutStats = {
  totalApplications: 0,
  citizensInformed: 0,
  locationsTracked: 0,
  lastUpdated: "",
};

const platformFeatures: Feature[] = [
  {
    icon: Globe,
    title: "Live planning updates",
    description:
      "Current planning applications are pulled into one searchable updates feed across the active council coverage.",
  },
  {
    icon: Brain,
    title: "Plain English summaries",
    description:
      "Application documents are translated into short, readable summaries that keep the important planning context intact.",
  },
  {
    icon: TrendingUp,
    title: "Signals and scale",
    description:
      "Categories, scale ratings and recurring signals help people understand what kind of change a filing may represent.",
  },
  {
    icon: Camera,
    title: "Document previews",
    description:
      "Planning drawings, forms and supporting documents remain close to the application record, not hidden behind portal friction.",
  },
  {
    icon: MapPin,
    title: "Spatial context",
    description:
      "Map views connect applications back to streets, neighbourhoods and local development patterns.",
  },
];

const technicalFeatures: Feature[] = [
  {
    icon: Database,
    title: "Data pipeline",
    description:
      "A backend ingestion system collects applications, normalises references and keeps public updates current.",
  },
  {
    icon: Cpu,
    title: "AI processing layer",
    description:
      "Document extraction, categorisation and summary generation sit behind Planning Updates to reduce manual planning noise.",
  },
  {
    icon: BarChart3,
    title: "Reporting engine",
    description:
      "The same structured records power weekly reports, analytics views and future planning alert workflows.",
  },
];

const futureFeatures: Feature[] = [
  ...(PLANNING_ALERTS_ENABLED
    ? [
        {
          icon: Bell,
          title: "Planning alerts",
          description:
            "Personalised notifications when new applications are submitted near a chosen address or area.",
        },
      ]
    : []),
  {
    icon: BarChart3,
    title: "Planning reports",
    description:
      "More exportable and repeatable reporting for residents, civic groups and professionals tracking local change.",
  },
  {
    icon: Camera,
    title: "Street-level context",
    description:
      "Better ways to connect planning applications to the real streets and buildings people recognise.",
  },
];

const pressMentions = [
  {
    outlet: "Deadline News",
    title: "Edinburgh local creates handy tool to view planning applications across city",
    description:
      "Coverage of City Scope as a user-friendly tool that makes Edinburgh planning applications easier to inspect.",
    href: "https://www.deadlinenews.co.uk/2025/06/06/edinburgh-local-creates-handy-tool-to-view-planning-applications-across-city/",
  },
  {
    outlet: "Edinburgh Minute",
    title: "A user-friendly way to find out about planning applications anywhere in Edinburgh",
    description:
      "Featured as a practical way for residents to understand what is being proposed in their neighbourhoods.",
    href: "https://edinburghminute.substack.com/p/edinburgh-minute-weekend-edition-6-june-2025",
  },
];

const routeCards = [
  {
    href: "/planning-updates",
    label: "Planning Updates",
    description: "Search live planning updates.",
    icon: Calendar,
  },
  {
    href: "/planning-map",
    label: "Planning Map",
    description: "Explore applications spatially.",
    icon: MapPin,
  },
  {
    href: "/planning-analytics",
    label: "Analytics",
    description: "Inspect wider planning trends.",
    icon: TrendingUp,
  },
];

const formatUpdatedAt = (value: string) => {
  if (!value) return "Live";

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value.includes("T") ? value.split("T")[0] : value;
  }

  return new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(parsed);
};

function SectionHeader({
  kicker,
  title,
  description,
}: {
  kicker: string;
  title: string;
  description?: string;
}) {
  return (
    <div className="max-w-3xl">
      <p className="pr-section-label">{kicker}</p>
      <h2 className="pr-heading mt-5 text-[34px] leading-tight sm:text-[44px]">{title}</h2>
      {description && (
        <p className="mt-5 text-base leading-7 text-[var(--c-text-secondary)]">{description}</p>
      )}
    </div>
  );
}

function FeatureCard({ icon: Icon, title, description }: Feature) {
  return (
    <article className="pr-panel h-full p-5">
      <span className="flex h-10 w-10 items-center justify-center border border-[var(--c-border)] bg-[var(--c-bg-secondary)] text-[var(--c-accent)]">
        <Icon className="h-5 w-5" strokeWidth={1.5} aria-hidden />
      </span>
      <h3 className="mt-7 font-['Newsreader'] text-2xl leading-tight text-[var(--c-text-primary)]">
        {title}
      </h3>
      <p className="mt-3 text-sm leading-6 text-[var(--c-text-secondary)]">{description}</p>
    </article>
  );
}

function StatPanel({ stats, loading }: { stats: AboutStats; loading: boolean }) {
  const items = [
    {
      label: "Applications indexed",
      value: loading
        ? "Loading"
        : stats.totalApplications > 0
        ? stats.totalApplications.toLocaleString()
        : "8,980",
    },
    {
      label: "Locations tracked",
      value: loading
        ? "Loading"
        : stats.locationsTracked > 0
        ? stats.locationsTracked.toLocaleString()
        : "4 councils",
    },
    {
      label: "Register status",
      value: formatUpdatedAt(stats.lastUpdated),
    },
  ];

  return (
    <dl className="grid border border-[var(--c-border)] bg-[var(--c-bg-secondary)] sm:grid-cols-3">
      {items.map((item, index) => (
        <div
          key={item.label}
          className={index > 0 ? "border-t border-[var(--c-border)] p-4 sm:border-l sm:border-t-0" : "p-4"}
        >
          <dt className="pr-data text-[9px] uppercase tracking-[0.14em]">{item.label}</dt>
          <dd className="pr-numeral mt-2 break-words text-2xl leading-tight">{item.value}</dd>
        </div>
      ))}
    </dl>
  );
}

export default function About() {
  const [stats, setStats] = useState<AboutStats>(defaultStats);
  const [isLoadingStats, setIsLoadingStats] = useState(true);

  useEffect(() => {
    let mounted = true;

    fetch("/api/about-stats")
      .then((response) => response.json())
      .then((data) => {
        if (!mounted) return;
        if (data && !data.error) {
          setStats({
            totalApplications: Number(data.totalApplications) || 0,
            citizensInformed: Number(data.citizensInformed) || 0,
            locationsTracked: Number(data.locationsTracked) || 0,
            lastUpdated: typeof data.lastUpdated === "string" ? data.lastUpdated : "",
          });
        }
      })
      .catch((error) => {
        console.error("Error fetching about stats:", error);
      })
      .finally(() => {
        if (mounted) setIsLoadingStats(false);
      });

    return () => {
      mounted = false;
    };
  }, []);

  return (
    <>
      <Meta
        title="About City Scope | Modern Civic Technology Platform"
        description="Learn about City Scope and the Summer of Planning launch, making local planning applications across Edinburgh, Midlothian, East Lothian and West Lothian understandable, accessible, and transparent."
        canonical="/about"
      />

      <div>
        <main className="planning-register min-h-screen bg-[var(--c-bg)] text-[var(--c-text-primary)]">
          <section className="mx-auto grid max-w-7xl gap-10 px-4 pb-14 pt-[calc(var(--navbar-height,64px)+3rem)] sm:px-6 lg:grid-cols-[minmax(0,1fr)_minmax(340px,440px)] lg:px-8 lg:pb-20 lg:pt-[calc(var(--navbar-height,64px)+4rem)]">
            <div className="min-w-0">
              <p className="pr-kicker pr-kicker--accent">CityScope / About</p>
              <h1 className="pr-masthead mt-5 max-w-4xl">
                Making <em>planning data</em> public-friendly.
              </h1>
              <p className="mt-6 max-w-2xl text-base leading-7 text-[var(--c-text-secondary)] sm:text-lg">
                City Scope is a civic technology platform built in Edinburgh to make local planning applications easier to search, read and understand. The current public coverage includes Edinburgh, Midlothian, East Lothian and West Lothian.
              </p>
              <p className="mt-4 max-w-2xl text-sm leading-6 text-[var(--c-text-secondary)]">
                {PLANNING_CURRENT_COVERAGE_COPY}{" "}
                <Link href="/planning-updates/wlo" className="text-[var(--c-accent)] underline decoration-[var(--c-border)] underline-offset-4 hover:decoration-[var(--c-accent)]">
                  Explore West Lothian planning updates.
                </Link>
              </p>
              <hr className="pr-double-rule mt-7 max-w-3xl" />
              <div className="mt-7 flex flex-wrap gap-3">
                <Link href="/planning-updates" className="pr-button">
                  Search Planning Updates
                  <Search className="h-4 w-4" strokeWidth={1.5} aria-hidden />
                </Link>
                <Link href="/planning-map" className="pr-button pr-button--ghost">
                  Open Planning Map
                  <ArrowRight className="h-4 w-4" strokeWidth={1.5} aria-hidden />
                </Link>
              </div>
            </div>

            <aside className="pr-panel overflow-hidden">
              <div className="border-b border-[var(--c-border)] p-4">
                <div className="pr-section-label">Current updates</div>
                <div className="mt-5 grid grid-cols-2 gap-3">
                  {[
                    [PLANNING_CATEGORY_ARTWORK.new_build_major, "New-build and major development"],
                    [PLANNING_CATEGORY_ARTWORK.trees_ecology, "Trees and ecology"],
                  ].map(([src, label]) => (
                    <div key={src} className="border border-[var(--c-border)] bg-[var(--c-bg-secondary)] p-3">
                      <Image
                        src={src}
                        alt={label}
                        width={180}
                        height={180}
                        className="h-32 w-full object-contain"
                        unoptimized
                      />
                    </div>
                  ))}
                </div>
              </div>
              <div className="p-4">
                <StatPanel stats={stats} loading={isLoadingStats} />
              </div>
            </aside>
          </section>

          <section className="border-y border-[var(--c-border)] bg-[var(--c-bg-secondary)]">
            <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
              <SectionHeader
                kicker="Why it exists"
                title="Planning information is public, but rarely public-friendly."
                description="Most official portals are slow to navigate and full of technical documents. City Scope keeps the public source record intact, then adds clearer summaries, categories, map context and repeatable search."
              />
            </div>
          </section>

          <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
            <SectionHeader
              kicker="What it does"
              title="A calmer front door for local planning."
              description="The product is built around the actual planning record: references, addresses, documents, decisions, summaries and geography."
            />
            <div className="mt-8 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {platformFeatures.map((feature) => (
                <FeatureCard key={feature.title} {...feature} />
              ))}
            </div>
          </section>

          <section className="border-y border-[var(--c-border)] bg-[var(--c-surface)]">
            <div className="mx-auto grid max-w-7xl gap-8 px-4 py-14 sm:px-6 lg:grid-cols-[minmax(0,1fr)_minmax(320px,420px)] lg:px-8">
              <div>
                <SectionHeader
                  kicker="Under the hood"
                  title="Built as a data product, not a brochure."
                  description="The public interface sits on top of a backend pipeline that collects records, prepares documents and exposes reusable planning data to the site."
                />
              </div>
              <div className="grid gap-3">
                {technicalFeatures.map((feature) => (
                  <FeatureCard key={feature.title} {...feature} />
                ))}
              </div>
            </div>
          </section>

          <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
            <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
              <SectionHeader
                kicker="Press mentions"
                title="Coverage from local media."
                description="City Scope has been covered as a practical tool for making planning applications easier to access and understand."
              />
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              {pressMentions.map((mention) => (
                <a
                  key={mention.href}
                  href={mention.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="pr-panel group block p-5 hover:border-[var(--c-text-tertiary)]"
                >
                  <span className="pr-data uppercase tracking-[0.14em] text-[var(--c-accent)]">
                    {mention.outlet}
                  </span>
                  <span className="mt-3 flex items-start justify-between gap-4 font-['Newsreader'] text-2xl leading-tight text-[var(--c-text-primary)]">
                    {mention.title}
                    <ExternalLink className="mt-1 h-4 w-4 flex-shrink-0 text-[var(--c-text-tertiary)] group-hover:text-[var(--c-accent)]" strokeWidth={1.5} aria-hidden />
                  </span>
                  <span className="mt-4 block text-sm leading-6 text-[var(--c-text-secondary)]">
                    {mention.description}
                  </span>
                </a>
              ))}
            </div>
          </section>

          <section className="border-y border-[var(--c-border)] bg-[var(--c-bg-secondary)]">
            <div className="mx-auto grid max-w-7xl gap-8 px-4 py-14 sm:px-6 lg:grid-cols-[minmax(0,1fr)_minmax(300px,420px)] lg:px-8">
              <div>
                <p className="pr-section-label">Built independently</p>
                <h2 className="pr-heading mt-5 text-[34px] leading-tight sm:text-[44px]">
                  A solo civic technology project from Edinburgh.
                </h2>
                <p className="mt-5 text-base leading-7 text-[var(--c-text-secondary)]">
                  City Scope is built by Harry Williams, a property professional and urban data enthusiast based in Edinburgh. The backend automation, AI integration, interface and map workflows have been designed and maintained independently.
                </p>
                <blockquote className="mt-7 border-l-2 border-[var(--c-accent)] pl-5 font-['Newsreader'] text-2xl leading-tight text-[var(--c-text-primary)]">
                  &quot;City Scope is my attempt to make local planning easier to access and understand.&quot;
                </blockquote>
              </div>
              <aside className="pr-panel p-5">
                <div className="pr-section-label">Founder links</div>
                <div className="mt-5 grid gap-3">
                  <a href="https://x.com/Harryjwilliamss" target="_blank" rel="noopener noreferrer" className="pr-button pr-button--ghost justify-start">
                    <Twitter className="h-4 w-4" strokeWidth={1.5} aria-hidden />
                    Twitter
                  </a>
                  <a href="https://www.linkedin.com/in/harryjwilliams/" target="_blank" rel="noopener noreferrer" className="pr-button pr-button--ghost justify-start">
                    <Linkedin className="h-4 w-4" strokeWidth={1.5} aria-hidden />
                    LinkedIn
                  </a>
                  <a href="mailto:harry9092@gmail.com?subject=City Scope Partnership Inquiry" className="pr-button justify-start">
                    <Mail className="h-4 w-4" strokeWidth={1.5} aria-hidden />
                    Contact
                  </a>
                </div>
              </aside>
            </div>
          </section>

          <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
            <SectionHeader
              kicker="What is next"
              title="More coverage, better reports, clearer local context."
              description="The next stage is about making repeated planning checks faster and expanding the product without losing the updates-first clarity."
            />
            <div className="mt-8 grid gap-4 md:grid-cols-3">
              {futureFeatures.map((feature) => (
                <FeatureCard key={feature.title} {...feature} />
              ))}
            </div>
          </section>

          <section className="border-t border-[var(--c-border)] bg-[var(--c-surface)]">
            <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
              <div className="pr-panel grid gap-6 p-6 sm:p-8 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-center">
                <div>
                  <p className="pr-section-label">Explore the platform</p>
                  <h2 className="pr-heading mt-4">Start with live updates.</h2>
                  <p className="mt-4 max-w-2xl text-sm leading-6 text-[var(--c-text-secondary)]">
                    The main product surfaces are Planning Updates, the map and analytics views.
                  </p>
                </div>
                <div className="grid gap-3 sm:grid-cols-3 lg:min-w-[520px]">
                  {routeCards.map((card) => {
                    const Icon = card.icon;
                    return (
                      <Link key={card.href} href={card.href} className="border border-[var(--c-border)] bg-[var(--c-bg-secondary)] p-4 hover:bg-[var(--c-surface-hover)]">
                        <Icon className="h-5 w-5 text-[var(--c-accent)]" strokeWidth={1.5} aria-hidden />
                        <span className="mt-4 block font-['Newsreader'] text-xl leading-tight text-[var(--c-text-primary)]">
                          {card.label}
                        </span>
                        <span className="mt-2 block text-xs leading-5 text-[var(--c-text-secondary)]">
                          {card.description}
                        </span>
                      </Link>
                    );
                  })}
                </div>
              </div>
            </div>
          </section>
        </main>
      </div>
    </>
  );
}
